import {useRef, useEffect, useState, useCallback} from 'react'
import { useNavigate } from 'react-router-dom'
import { useInterviewStore } from '@/store/interviewStore'
import { useJoinStore } from '@/store/joinStore'
import { AvatarVideo } from '@/components/interview/AvatarVideo'
import { SelfVideo } from '@/components/interview/SelfVideo'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { t } from '@/i18n/translations'
import { useInterviewSessionContext } from '@/context/InterviewSessionContext'
import { SettingsPanel } from '@/components/interview/SettingsPanel'
import { useDeviceManager } from '@/hooks/useDeviceManager'
import { useBranding } from '@/hooks/useBranding'
import { classifyGetUserMediaError } from '@/utils/media'
import type { MediaErrorCode } from '@/store/interviewStore'
import type { TranslationKey } from '@/i18n/translations'
import { SHOW_CANDIDATE_TRANSCRIPT } from '@/config/features'

const MEDIA_ERROR_KEY: Record<MediaErrorCode, TranslationKey> = {
  permission_denied: 'mediaPermissionDenied',
  no_device: 'mediaNoDevice',
  device_busy: 'mediaDeviceBusy',
  unknown: 'mediaStartFailed',
}

type Message = { role: 'ai' | 'user'; text: string }


function formatTime(secs: number): string {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function InterviewScreen() {
  const { language, interview, handleStart, handleNext, handleEnd } = useInterviewSessionContext()
  const store = useInterviewStore()
  const navigate = useNavigate()
  const branding = useBranding()
  const avatarProvider = useJoinStore((s) => s.resolved?.avatar.provider)
  const isLemonSlice = avatarProvider === 'lemonslice'

  const avatarVideoRef = useRef<HTMLVideoElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const [messageHistory, setMessageHistory] = useState<Message[]>([])
  const [pipMinimized, setPipMinimized] = useState(false)
  const [transcriptMinimized, setTranscriptMinimized] = useState(false)
  const [transcriptFontSize, setTranscriptFontSize] = useState(14)
  const [micEnabled, setMicEnabled] = useState(true)
  const [cameraEnabled, setCameraEnabled] = useState(true)
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const userScrolledUpRef = useRef(false)

  // Mobile transcript drag-to-resize
  const [transcriptHeight, setTranscriptHeight] = useState<number | null>(null)
  const dragStartY = useRef(0)
  const dragStartHeight = useRef(0)
  const transcriptRef = useRef<HTMLDivElement>(null)

  const handleDragStart = (clientY: number) => {
    dragStartY.current = clientY
    const el = transcriptRef.current
    if (el) dragStartHeight.current = el.getBoundingClientRect().height
  }

  const handleDragMove = (clientY: number) => {
    const delta = dragStartY.current - clientY
    const newHeight = Math.max(80, Math.min(window.innerHeight * 0.75, dragStartHeight.current + delta))
    setTranscriptHeight(newHeight)
  }

  const dragHandleRef = useRef<HTMLDivElement>(null)

  // Attach touch listeners with { passive: false } so preventDefault works on iOS
  useEffect(() => {
    const el = dragHandleRef.current
    if (!el) return
    const onStart = (e: TouchEvent) => { e.preventDefault(); handleDragStart(e.touches[0].clientY) }
    const onMove = (e: TouchEvent) => { e.preventDefault(); handleDragMove(e.touches[0].clientY) }
    el.addEventListener('touchstart', onStart, { passive: false })
    el.addEventListener('touchmove', onMove, { passive: false })
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchmove', onMove) }
  })

  // Mobile PiP drag-to-reposition (direct DOM manipulation for smooth 60fps dragging)
  const [pipPos, setPipPos] = useState<{ x: number; y: number } | null>(null)
  const pipDragStart = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null)
  const pipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = pipRef.current
    if (!el) return
    const onStart = (e: TouchEvent) => {
      if (window.innerWidth > 767) return
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const t = e.touches[0]
      pipDragStart.current = { px: t.clientX, py: t.clientY, ox: rect.left, oy: rect.top }
      el.style.willChange = 'transform'
    }
    const onMove = (e: TouchEvent) => {
      e.preventDefault()
      const s = pipDragStart.current
      if (!s) return
      const t = e.touches[0]
      const dx = t.clientX - s.px
      const dy = t.clientY - s.py
      el.style.transform = `translate(${dx}px, ${dy}px)`
    }
    const onEnd = () => {
      const s = pipDragStart.current
      if (!s) return
      // Compute final position, apply directly to DOM, then sync React state
      const rect = el.getBoundingClientRect()
      const newX = Math.max(0, Math.min(window.innerWidth - 80, rect.left))
      const newY = Math.max(0, Math.min(window.innerHeight - 80, rect.top))
      // Set position inline BEFORE removing transform — no visual jump
      el.style.position = 'fixed'
      el.style.left = `${newX}px`
      el.style.top = `${newY}px`
      el.style.right = 'auto'
      el.style.bottom = 'auto'
      el.style.transform = ''
      el.style.willChange = ''
      pipDragStart.current = null
      setPipPos({ x: newX, y: newY })
    }
    el.addEventListener('touchstart', onStart, { passive: false })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchmove', onMove); el.removeEventListener('touchend', onEnd) }
  })

  const deviceManager = useDeviceManager({
    streamRef: interview.streamRef,
    audioElRef: interview.audioElRef,
    replaceLocalTrack: interview.replaceLocalTrack,
  })

  // Request camera/mic permissions early so users can preview and configure devices
  // before clicking "Interview starten". A failure here (permission denied,
  // no camera) is the same failure Start would hit — surface it right away.
  useEffect(() => {
    interview.requestPreview().then(() => {
      useInterviewStore.getState().setMediaError(null)
      deviceManager.enumerateAll()
    }).catch((err) => {
      console.warn('Preview stream request failed:', err)
      useInterviewStore.getState().setMediaError(classifyGetUserMediaError(err))
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect if no interview config is present (direct URL access without setup)
  useEffect(() => {
    if (!store.config) {
      navigate('/', { replace: true })
    }
  }, [store.config, navigate])

  // Timing-based avatar text reveal driven by word boundaries from TTS
  const { displayed: avatarDisplayed, complete: completeAvatar } = interview.textStream

  // Always-fresh ref to avatarDisplayed — updated every render (outside effects) so the
  // archive effect can read the value from the same render cycle that triggered it.
  const latestAvatarDisplayedRef = useRef(avatarDisplayed)
  latestAvatarDisplayedRef.current = avatarDisplayed

  // Phase-specific display logic:
  // - avatar_speaking: timing-based reveal
  // - user_response: empty — script is archived into messageHistory, not shown again here
  // - avatar_reacting: timing-based or 'Processing...' until reaction text arrives
  // - other phases: agentMessage directly
  // const isAvatarPhase = store.currentPhase === 'avatar_speaking' || store.currentPhase === 'avatar_reacting'
  const displayed = (() => {
    const phase = store.currentPhase
    if (phase === 'avatar_speaking') return avatarDisplayed
    if (phase === 'user_response') return ''
    if (phase === 'avatar_reacting') return avatarDisplayed || store.agentMessage
    return store.agentMessage
  })()
  const visibleCandidateTranscript = SHOW_CANDIDATE_TRANSCRIPT
    ? store.realtimeTranscript
    : ''

  // Sync video ref to LiveKit hook and re-attach any already-subscribed
  // tracks. Deliberately dep-less (the element can mount late), but gated on
  // the element actually changing — otherwise every render (each transcript
  // word) re-ran attach()/play() against the same element.
  useEffect(() => {
    if (avatarVideoRef.current && interview.videoElRef.current !== avatarVideoRef.current) {
      interview.videoElRef.current = avatarVideoRef.current
      interview.reattachTracks()
    }
  })

  // Complete timing-based reveal when leaving both avatar phases
  useEffect(() => {
    const phase = store.currentPhase
    if (phase !== 'avatar_speaking' && phase !== 'avatar_reacting') completeAvatar()
  }, [store.currentPhase]) // eslint-disable-line react-hooks/exhaustive-deps

  // Archive messages on phase transitions — single source of truth, no duplicates
  const prevPhaseRef = useRef(store.currentPhase)
  useEffect(() => {
    const phase = store.currentPhase
    const prev = prevPhaseRef.current

    // AI script finished → archive it using the always-fresh ref (not prevDisplayedRef, which
    // would be stale because complete() and setPhase() land in the same React render batch).
    if (prev === 'avatar_speaking' && phase === 'user_response') {
      const scriptText = latestAvatarDisplayedRef.current
      if (scriptText?.trim()) {
        setMessageHistory((h) => [...h, { role: 'ai', text: scriptText }])
      }
    }

    // User answer → archive their partial transcript
    if (
      SHOW_CANDIDATE_TRANSCRIPT &&
      prev === 'user_response' &&
      phase === 'avatar_reacting'
    ) {
      const userText = store.realtimeTranscript
      if (userText) {
        setMessageHistory((h) => [...h, { role: 'user', text: userText }])
      }
    }

    // Reaction finished → archive the AI reaction text
    if (prev === 'avatar_reacting' && phase === 'avatar_speaking') {
      const reactionText = interview.reactionTextRef.current
      if (reactionText?.trim()) {
        setMessageHistory((h) => [...h, { role: 'ai', text: reactionText }])
      }
      interview.reactionTextRef.current = ''
    }

    prevPhaseRef.current = phase
  }, [store.currentPhase, store.realtimeTranscript]) // eslint-disable-line react-hooks/exhaustive-deps

  // When the backend delivers the LLM-cleaned transcript, retroactively replace the
  // last user message in the conversation panel with the corrected version.
  useEffect(() => {
    const cleaned = store.cleanedTranscript
    if (!cleaned) return
    if (SHOW_CANDIDATE_TRANSCRIPT) {
      setMessageHistory((h) => {
        const lastUserIdx = h.map((m) => m.role).lastIndexOf('user')
        if (lastUserIdx === -1) return h
        return h.map((m, i) => (i === lastUserIdx ? { ...m, text: cleaned } : m))
      })
    }
    store.setCleanedTranscript(null)
  }, [store.cleanedTranscript]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track manual scroll — stop auto-scrolling when user reads history
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      userScrolledUpRef.current = el.scrollHeight - el.scrollTop - el.clientHeight > 60
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Auto-scroll when new content appears — both archived messages and live word reveal
  useEffect(() => {
    const el = scrollRef.current
    if (!el || userScrolledUpRef.current) return
    el.scrollTop = el.scrollHeight
  }, [messageHistory, displayed, visibleCandidateTranscript])

  const getDisplayText = useCallback(() => displayed, [displayed])

  // Action button logic
  const isReady = store.status === 'ready'
  const isRunning = store.status === 'running'
  const actionLabel = isRunning ? t(language, 'next') : t(language, 'startInterview')
  // Avatar gate: when avatars are enabled, also wait for BEY's video track
  // to attach before allowing Start. Falls back to true after the 20s
  // timeout in the setup hook so the button never gets stuck forever.
  // Additionally:
  // - issue #22 Phase 1: if BEY is at capacity, the button stays disabled
  //   — the candidate must refresh.
  // - issue #22 Phase 2: if the worker is queued (waiting for a BEY slot),
  //   the button is also disabled — they'll see the queue overlay until a
  //   slot opens and the avatar video lands.
  // While avatarDegraded is true the candidate hasn't yet acknowledged
  // they want to continue without the avatar — gate Start until they
  // click the "Continue without avatar" button on the degraded overlay.
  const avatarGateOpen =
    !store.avatarEnabled || (
      store.avatarReady &&
      !store.avatarUnavailable &&
      !store.avatarDegraded &&
      store.queuePosition === null
    )
  const actionEnabled =
    (isReady && avatarGateOpen) ||
    (isRunning && store.currentPhase === 'user_response')

  // We're still establishing the avatar connection: ready to start, avatar
  // expected, but its track hasn't attached yet and no overlay (queue /
  // unavailable / degraded) is claiming the screen. In this window the Start
  // button is disabled, and the default disabled styling reads as clickable on
  // the dark UI — so show an explicit "connecting…" label + spinner instead of
  // a button that looks live but swallows clicks.
  const avatarConnecting =
    isReady &&
    store.avatarEnabled &&
    !store.avatarReady &&
    !store.avatarUnavailable &&
    !store.avatarDegraded &&
    store.queuePosition === null

  function handleAction() {
    if (isReady) handleStart()
    else if (isRunning && store.currentPhase === 'user_response') handleNext()
  }

  // Progress %
  const segments = store.segments.length > 0
    ? store.segments
    : (store.config?.mode === 'demo' ? ['intro', 'q1', 'outro'] : ['intro', 'q1', 'q2', 'outro'])
  const totalSegments = segments.length
  const segIdx = store.currentSegment ? segments.indexOf(store.currentSegment) : -1
  const progressPct = segIdx >= 0 ? Math.round(((segIdx + 1) / totalSegments) * 100) : 0

  // Mic toggle — enables/disables audio tracks on the media stream
  function toggleMic() {
    const stream = interview.streamRef.current
    if (!stream) return
    const next = !micEnabled
    stream.getAudioTracks().forEach((track) => { track.enabled = next })
    setMicEnabled(next)
  }

  // Camera toggle — enables/disables video tracks on the media stream
  function toggleCamera() {
    const stream = interview.streamRef.current
    if (!stream) return
    const next = !cameraEnabled
    stream.getVideoTracks().forEach((track) => { track.enabled = next })
    setCameraEnabled(next)
  }

  if (!store.config) return null

  const avatarLayer = (
    <div className={`iv-avatar-bg${isLemonSlice ? ' iv-avatar-bg--contained' : ''}`}>
      <AvatarVideo
        ref={avatarVideoRef}
        avatarEnabled={store.avatarEnabled}
        avatarReady={store.avatarReady}
        avatarUnavailable={store.avatarUnavailable}
        avatarDegraded={store.avatarDegraded}
        onContinueDegraded={() => store.setAvatarDegraded(false)}
        queuePosition={store.queuePosition}
        queueEtaSeconds={store.queueEtaSeconds}
        queueDepth={store.queueDepth}
        isConnecting={store.status === 'idle' || store.status === 'setup'}
        language={language}
        fitMode={isLemonSlice ? 'contain' : 'cover'}
      />
    </div>
  )

  return (
    <div className="iv-screen">
      {/* Fullscreen avatar background */}
      {!isLemonSlice && avatarLayer}

      {/* Top bar */}
      <div className="iv-top-bar">
        <div className="iv-logo">
          <img className="iv-logo-icon" src="/images/logo.png" alt="Tembo" />
          <span>TEMBO</span>
        </div>
        <div className="iv-top-spacer" />
        {branding.jobTitle && (
          <div className="iv-top-center">
            {branding.companyName && <>
              <span className="iv-top-role">{branding.companyName}</span>
              <span className="iv-top-divider">·</span>
            </>}
            <span>{branding.jobTitle}</span>
          </div>
        )}
        <div className="iv-top-spacer" />
        <div className="iv-top-meta">
          {isRunning && <span className="iv-live-dot" />}
          {isRunning && <span className="iv-timer">{formatTime(store.recordingElapsed)}</span>}
        </div>
      </div>

      {/* Media error banner — getUserMedia failed (permission denied, no
          device, device busy). Without this the Start button visibly does
          nothing, which is the most common real-world failure of the app. */}
      {store.mediaError && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            top: 72,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 30,
            maxWidth: 'min(92vw, 560px)',
            padding: '10px 16px',
            borderRadius: 8,
            background: 'rgba(127, 29, 29, 0.92)',
            color: '#fecaca',
            fontSize: 13,
            lineHeight: 1.4,
            textAlign: 'center',
          }}
        >
          {t(language, MEDIA_ERROR_KEY[store.mediaError])}
        </div>
      )}

      {/* Content area */}
      <div className="iv-content">
        {/* LemonSlice stays inside the space between the header and controls. */}
        {isLemonSlice && avatarLayer}

        {/* Candidate PiP — hidden before interview starts */}
        {isRunning && (
          <div
            ref={pipRef}
            className={`iv-pip${pipMinimized ? ' iv-pip--minimized' : ''}`}
            style={pipPos ? {
              position: 'fixed',
              top: pipPos.y,
              left: pipPos.x,
              right: 'auto',
              bottom: 'auto',
              touchAction: 'none',
            } : { touchAction: 'none' }}
          >
            {cameraEnabled
              ? <SelfVideo streamRef={interview.streamRef} isRecording={isRunning} />
              : <div className="iv-pip-cam-off">📷</div>
            }
            {!pipMinimized && <div className="iv-pip-label">{t(language, 'candidateLabel')}</div>}
            <button
              className="iv-pip-minimize"
              onClick={() => setPipMinimized((v) => !v)}
              title={pipMinimized ? t(language, 'pipEnlarge') : t(language, 'pipMinimize')}
            >
              <svg viewBox="0 0 24 24">
                {pipMinimized
                  ? <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  : <line x1="5" y1="12" x2="19" y2="12" />
                }
              </svg>
            </button>
          </div>
        )}

        {/* Transcript panel */}
        {!transcriptMinimized ? (
          <div
            className="iv-transcript"
            ref={transcriptRef}
            style={{
              fontSize: transcriptFontSize,
              ...(transcriptHeight != null ? { height: transcriptHeight } : {}),
            }}
          >
            <div className="iv-transcript-drag" ref={dragHandleRef}>
              <span className="iv-transcript-drag-bar" />
            </div>
            <div className="iv-transcript-scroll" ref={scrollRef}>
              {messageHistory
                .filter((m) => SHOW_CANDIDATE_TRANSCRIPT || m.role === 'ai')
                .map((m, i) => (
                  <div key={i} className={`iv-msg iv-msg--${m.role === 'ai' ? 'ai' : 'user'}`}>
                    {m.text}
                  </div>
                ))}
              {displayed && (
                <div className="iv-msg iv-msg--current">
                  {getDisplayText()}
                </div>
              )}
              {store.currentPhase === 'user_response' && (
                <div className="iv-msg iv-msg--hint">
                  {store.config?.language === 'de'
                    ? 'Bitte gib deine Antwort und klicke dann auf "Weiter"'
                    : 'Please give your answer then click "Next"'}
                </div>
              )}
              {store.currentPhase === 'user_response' && visibleCandidateTranscript && (
                <div className="iv-msg iv-msg--user iv-msg--partial">
                  {visibleCandidateTranscript}
                </div>
              )}
            </div>
            <button
              className="iv-layer-minimize"
              onClick={() => setTranscriptMinimized(true)}
              title={t(language, 'transcriptHide')}
            >
              <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /></svg>
            </button>
            <div className="iv-transcript-zoom">
              <button
                  className="iv-zoom-btn"
                  onClick={() => setTranscriptFontSize((s) => Math.max(10, s - 2))}
                  title={t(language, 'fontDecrease')}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="6" y1="12" x2="18" y2="12" />
                </svg>
              </button>
              <span className="iv-zoom-label">aA</span>
              <button
                  className="iv-zoom-btn"
                  onClick={() => setTranscriptFontSize((s) => Math.min(24, s + 2))}
                  title={t(language, 'fontIncrease')}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="6" y1="12" x2="18" y2="12" />
                  <line x1="12" y1="6" x2="12" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <button
            className="iv-transcript-restore"
            onClick={() => setTranscriptMinimized(false)}
          >
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {t(language, 'transcriptLabel')}
          </button>
        )}
      </div>

      {/* Bottom bar */}
      <div className="iv-bottom-bar">
        <div className="iv-bottom-left">
          {isRunning && (
            <>
              <span>{t(language, 'progress')}</span>
              <span className="iv-progress-pct">{progressPct}%</span>
            </>
          )}
        </div>

        <div className="iv-controls-center">
          {/* Mic toggle */}
          <button
            className={`iv-ctrl${!micEnabled ? ' iv-ctrl--muted' : ''}`}
            title={micEnabled ? t(language, 'micDisable') : t(language, 'micEnable')}
            onClick={toggleMic}
          >
            {micEnabled ? (
              <svg viewBox="0 0 24 24">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>

          {/* Settings gear */}
          <button
            className={`iv-ctrl${showSettings ? ' iv-ctrl--active' : ''}`}
            title={t(language, 'settings')}
            onClick={() => setShowSettings((v) => !v)}
          >
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>

          {/* End — available from ready so candidates can abort before starting */}
          {(isReady || isRunning) && (
            <button className="iv-ctrl iv-ctrl--end" title={t(language, 'end')} onClick={() => setShowEndConfirm(true)}>
              <svg viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <SettingsPanel
          open={showSettings}
          onClose={() => setShowSettings(false)}
          language={language}
          cameraEnabled={cameraEnabled}
          onToggleCamera={toggleCamera}
          streamRef={interview.streamRef}
          devices={deviceManager.devices}
          selectedAudioInputId={deviceManager.selectedAudioInputId}
          selectedVideoInputId={deviceManager.selectedVideoInputId}
          selectedAudioOutputId={deviceManager.selectedAudioOutputId}
          sinkIdSupported={deviceManager.sinkIdSupported}
          onSwitchAudioInput={deviceManager.switchAudioInput}
          onSwitchVideoInput={deviceManager.switchVideoInput}
          onSwitchAudioOutput={deviceManager.switchAudioOutput}
          onPlayTestTone={deviceManager.playTestTone}
        />

        <div className="iv-bottom-right">
          <button
            className={`iv-btn-next${avatarConnecting ? ' iv-btn-next--connecting' : ''}`}
            disabled={!actionEnabled}
            onClick={handleAction}
          >
            {avatarConnecting ? (
              <>
                <span className="iv-btn-spinner" aria-hidden="true" />
                <span>{t(language, 'connecting')}</span>
              </>
            ) : (
              <>
                <span>{actionLabel}</span>
                <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
              </>
            )}
          </button>
        </div>
      </div>
      <ConfirmModal
        open={showEndConfirm}
        onClose={() => setShowEndConfirm(false)}
        onConfirm={() => { setShowEndConfirm(false); handleEnd() }}
        title={t(language, 'endConfirmTitle')}
        message={t(language, 'endConfirmMessage')}
        confirmLabel={t(language, 'end')}
        cancelLabel={t(language, 'cancel')}
        severity="danger"
      />
      {store.currentPhase === 'finalizing' && (
        <div className="iv-finalizing-overlay">
          <div className="iv-finalizing-content">
            <div className="iv-finalizing-spinner" />
            <span className="iv-finalizing-text">{t(language, 'finalizingInterview')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
